import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isFailed } from 'redux/toolkit/api'
import { setErrorSnackBar, setWarningSnackBar } from 'redux/features/app/appSlice'

// Maps OAuth error codes to snackbar i18n keys (under ess.snackbar.*)
const ERROR_KEY_MAP: Record<string, string> = {
  invalid_state: 'bcc_auth_error_invalid_state',
  missing_code_verifier: 'bcc_auth_error_missing_code_verifier',
  missing_code: 'bcc_auth_error_missing_code',
  token_exchange_failed: 'bcc_auth_error_token_exchange_failed',
  access_denied: 'bcc_auth_error_access_denied'
}

const UNKNOWN_ERROR_KEY = 'bcc_auth_error_unknown'
const SESSION_EXPIRED_KEY = 'bcc_auth_session_expired'

export function useBccAuthErrorNotifications(): void {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const hasShownSessionExpiredRef = useRef(false)

  const sessionValidationFailed = useAppSelector(_store => isFailed(_store.bccAuth.api.sessionValidationStatus))

  // Handle OAuth callback errors from query param
  useEffect(() => {
    const bccAuthError = searchParams.get('bcc_auth_error')
    if (!bccAuthError) return

    const snackbarKey = ERROR_KEY_MAP[bccAuthError] || UNKNOWN_ERROR_KEY
    dispatch(
      setErrorSnackBar({
        message: snackbarKey,
        autoHideDuration: 6000
      })
    )

    // Clear the error param from URL
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('bcc_auth_error')
    setSearchParams(newParams, { replace: true })
  }, [searchParams, dispatch, setSearchParams])

  // Handle session validation failures
  useEffect(() => {
    if (sessionValidationFailed && !hasShownSessionExpiredRef.current) {
      hasShownSessionExpiredRef.current = true
      dispatch(
        setWarningSnackBar({
          message: SESSION_EXPIRED_KEY,
          autoHideDuration: 4000
        })
      )
    }

    // Reset flag when session becomes valid again
    if (!sessionValidationFailed) {
      hasShownSessionExpiredRef.current = false
    }
  }, [sessionValidationFailed, dispatch])
}

export default useBccAuthErrorNotifications
